import React, { useState, useEffect } from 'react';
import { contentAPI } from '../../services/api';
import { FaFileAlt, FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const CommuniquesSection = () => {
    const { language, t } = useLanguage();
    const [communiques, setCommuniques] = useState([]);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await contentAPI.getCommuniques();
            setCommuniques(res.data || res || []);
        } catch (error) {
            console.error('Erreur chargement:', error);
            setCommuniques([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            data.append('title', formData.title || '');
            data.append('title_ar', formData.title_ar || '');
            data.append('content', formData.content || '');
            data.append('content_ar', formData.content_ar || '');
            if (formData.pdf) data.append('pdf', formData.pdf);

            if (formData.id) {
                await contentAPI.updateCommunique(formData.id, data);
            } else {
                await contentAPI.createCommunique(data);
            }
            loadData();
            setFormData({});
            alert('✅ Communiqué enregistré!');
        } catch (error) {
            const msg = error.response?.data?.error || error.message || 'Erreur inconnue';
            alert(`❌ Erreur lors de l'enregistrement: ${msg}`);
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer ce communiqué?')) {
            try {
                await contentAPI.deleteCommunique(id);
                loadData();
            } catch (error) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>Chargement...</p>
            </div>
        );
    }

    return (
        <div>
            <h1 style={styles.title}>📢 {language === 'ar' ? 'القوانين واللوائح' : 'Lois et Règlements'}</h1>

            <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                    {formData.id ? t('edit') : t('add')} {language === 'ar' ? 'بلاغ' : 'un communiqué'}
                </h2>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.row}>
                        <div style={styles.col}>
                            <label style={styles.label}>Titre (FR)</label>
                            <input
                                type="text"
                                value={formData.title || ''}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                style={styles.input}
                                placeholder="Titre en français"
                                required
                            />
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>العنوان (AR)</label>
                            <input
                                type="text"
                                value={formData.title_ar || ''}
                                onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                                style={{ ...styles.input, direction: 'rtl' }}
                                placeholder="العنوان بالعربية"
                            />
                        </div>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.col}>
                            <label style={styles.label}>Contenu (FR)</label>
                            <textarea
                                value={formData.content || ''}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                style={{ ...styles.input, minHeight: '120px' }}
                                placeholder="Contenu en français"
                            />
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>المحتوى (AR)</label>
                            <textarea
                                value={formData.content_ar || ''}
                                onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
                                style={{ ...styles.input, minHeight: '120px', direction: 'rtl' }}
                                placeholder="المحتوى بالعربية"
                            />
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Document PDF</label>
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData({ ...formData, pdf: e.target.files[0] })}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formActions}>
                        <button type="submit" style={styles.submitBtn}>
                            {formData.id ? t('edit') : (language === 'ar' ? 'نشر' : 'Publier')}
                        </button>
                        {formData.id && (
                            <button type="button" onClick={() => setFormData({})} style={styles.cancelBtn}>
                                {t('cancel')}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div style={styles.card}>
                <h2 style={styles.cardTitle}>Liste des communiqués ({communiques.length})</h2>
                {communiques.length === 0 ? (
                    <div style={styles.emptyState}>
                        <p>Aucun communiqué trouvé</p>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {communiques.map(comm => (
                            <div key={comm.id} style={styles.commCard}>
                                <div style={styles.commInfo}>
                                    <FaFileAlt style={{ color: '#c41e3a', fontSize: '24px' }} />
                                    <div>
                                        <h3 style={styles.commTitle}>{comm.title}</h3>
                                        <p style={styles.commTitleAr}>{comm.title_ar}</p>
                                        <p style={styles.commDate}>{new Date(comm.published_at).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div style={styles.commActions}>
                                    <button onClick={() => setFormData(comm)} style={styles.editBtn}>
                                        <FaEdit /> Modifier
                                    </button>
                                    <button onClick={() => handleDelete(comm.id)} style={styles.deleteBtn}>
                                        <FaTrash /> Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    title: { fontSize: '32px', fontWeight: 'bold', marginBottom: '20px' },
    card: { background: 'white', padding: '30px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
    cardTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
    input: { padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    col: { display: 'flex', flexDirection: 'column', gap: '8px' },
    formActions: { display: 'flex', gap: '10px' },
    submitBtn: { padding: '15px', background: '#c41e3a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', flex: 1 },
    cancelBtn: { padding: '15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
    grid: { display: 'flex', flexDirection: 'column', gap: '15px' },
    commCard: { background: '#f9f9f9', padding: '20px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e0e0e0' },
    commInfo: { display: 'flex', gap: '20px', alignItems: 'center' },
    commTitle: { fontSize: '18px', fontWeight: 'bold', margin: '0 0 5px 0' },
    commTitleAr: { fontSize: '16px', color: '#666', margin: '0 0 5px 0', direction: 'rtl' },
    commDate: { fontSize: '13px', color: '#999', margin: 0 },
    commActions: { display: 'flex', gap: '10px' },
    editBtn: { padding: '8px 15px', background: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' },
    deleteBtn: { padding: '8px 15px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px' },
    spinner: { width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    emptyState: { textAlign: 'center', padding: '40px', color: '#999' }
};

export default CommuniquesSection;
